// 0x1F1E6-0x1F1FF
const regionalIndicatorRegex = /^(\uD83C[\uDDE6-\uDDFF])+$/;
const subdivisionFlagRegex = /^\uD83C\uDFF4\uDB40.+$/;

function notoExcluded(symbol: string) {
    return (
        regionalIndicatorRegex.test(symbol) || subdivisionFlagRegex.test(symbol)
    );
}

export function patchEmojiToNotoHtml(
    textContent: string,
    width: number | undefined = undefined,
    height: number | undefined = undefined
) {
    return extractEmojiData(textContent)
        .map((x) => {
            if (x.type === "text") {
                return htmlEncode(x.content);
            }
            return createNotoImgTagForSymbol(x.content, width, height);
        })
        .join("");
}

function htmlEncode(text: string) {
    return text.replace(
        /[\u00A0-\u9999<>\&]/g,
        (i) => "&#" + i.charCodeAt(0) + ";"
    );
}

type ExtractedEntry = { type: "text" | "emoji"; content: string };

export function extractEmojiData(textContent: string): ExtractedEntry[] {
    const matches = Array.from(
        textContent.matchAll(
            /(?<emoji>\p{RI}\p{RI}|\p{Emoji}(\p{EMod}+|\u{FE0F}\u{20E3}?|[\u{E0020}-\u{E007E}]+\u{E007F})?(\u{200D}\p{Emoji}(\p{EMod}+|\u{FE0F}\u{20E3}?|[\u{E0020}-\u{E007E}]+\u{E007F})?)+|\p{EPres}(\p{EMod}+|\u{FE0F}\u{20E3}?|[\u{E0020}-\u{E007E}]+\u{E007F})?|\p{Emoji}(\p{EMod}+|\u{FE0F}\u{20E3}?|[\u{E0020}-\u{E007E}]+\u{E007F}))/gu
        )
    ).map((x) => ({
        index: x.index,
        length: x.groups!.emoji.length,
        content: x.groups!.emoji,
    }));

    const result: (ExtractedEntry & { index: number; length: number })[] =
        matches.map((x) => ({
            type: "emoji",
            content: x.content,
            index: x.index!,
            length: x.length,
        }));
    const length = result.length;
    let shift = 0;
    for (let i = 0; i < length; i++) {
        const actualI = i + shift;
        const currentItem = result[actualI];
        const lastItem = i > 0 ? result[actualI - 1] : undefined;
        const missingBeforeFrom =
            lastItem != null ? lastItem.index + lastItem.length : 0;
        const missingBeforeTo = currentItem.index;
        if (missingBeforeTo > missingBeforeFrom) {
            result.splice(actualI, 0, {
                type: "text",
                content: textContent.substring(
                    missingBeforeFrom,
                    missingBeforeTo
                ),
                index: missingBeforeFrom,
                length: missingBeforeTo - missingBeforeFrom,
            });
            shift++;
        }
    }
    const missingBeforeFrom =
        result.length === 0
            ? 0
            : result[result.length - 1].index +
              result[result.length - 1].length;
    if (missingBeforeFrom < textContent.length - 1) {
        result.push({
            type: "text",
            content: textContent.substring(missingBeforeFrom),
            index: missingBeforeFrom,
            length: textContent.length - missingBeforeFrom,
        });
    }

    return result;
}

const notoEmojiUrlStart = "https://cdn.odendo.com/resources/noto-emoji/emoji_";
const notoEmojiUrlEnd = ".svg";

function zeroFillU(s: string) {
    if (s.length >= 4) return s;
    return "0".repeat(4 - s.length) + s;
}

export function createNotoSymbolCode(symbol: string) {
    if (notoExcluded(symbol)) return undefined;
    return (
        "u" +
        [...symbol]
            .map((cp) => cp.codePointAt(0))
            .filter((x) => x != null && x != 0xfe0f)
            .map((cp) => zeroFillU(cp!.toString(16)))
            .join("_")
            .toLowerCase()
    );
}

export function createNotoImgUrlForSymbol(symbol: string) {
    const symbolCode = createNotoSymbolCode(symbol);
    return `${notoEmojiUrlStart}${symbolCode}${notoEmojiUrlEnd}`;
}

export function createNotoImgTagForSymbol(
    symbol: string,
    width?: number,
    height?: number
) {
    const symbolCode = createNotoSymbolCode(symbol);
    // <img src="https://cdn.odendo.com/resources/noto-emoji/emoji_u2665.svg" width="16" height="16" data-no-block="" style="vertical-align: -px;" alt="♥️">
    let result = `<img src="${notoEmojiUrlStart}${symbolCode}${notoEmojiUrlEnd}"`;
    if (width != null && typeof width == "number" && width > 0) {
        result += ` width="${width}"`;
    }
    if (height != null && typeof height == "number" && height > 0) {
        result += ` height="${height}"`;
    }
    result += ` data-no-block style="vertical-align: -0.125em;" alt="${symbol}">`;
    return result;
}
